import { Formik, FormikHelpers, FormikProps } from "formik";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { applyApiErrors, VALIDATION_TYPES } from "@/components/Formik";
import { Header as ModalHeader } from "@/components/Header";
import { LoadingSpinner } from "@/components/Loading";
import { errorEntryType, hasCode, hasFieldCode } from "@/core/api";
import { passwordSetup } from "@/data/session";
import { Flex, Header, Img, MessagesContext } from "@/eworkitComponents";
import { RouteButton, withRouteProps } from "@/infrastructure/Route";
import { AnimatedLayoutContainer } from "@/views/components/Layout";
import { router } from "@/views/router";

import { transformPasswordSetupRouteProps } from "../../selectors";
import { IPasswordSetupRouteProps } from "../../types";
import { usePasswordStatus } from "../hooks/PasswordStatus";
import {
  FORM_EMPTY_INITIAL_VALUES,
  FORM_MESSENGER_CONTEXT,
  IPasswordSetupFormValues,
  PasswordResetTokenInvalid,
  PasswordSetupForm,
  validationSchema,
} from "./components";
import CongratulationImg from "./congratulation.svg";

export interface IPasswordSetupProps extends IPasswordSetupRouteProps {}

export const SessionPasswordSetupComponent: React.FunctionComponent<IPasswordSetupProps> = React.memo(
  ({ routeProps: { token } }) => {
    const [sent, setSent] = useState(false);
    const { addMessage } = useContext(MessagesContext);
    const { t } = useTranslation(["public", "common"]);
    const passwordStatus = usePasswordStatus(token);

    const handleSubmit = async (values: IPasswordSetupFormValues, actions: FormikHelpers<IPasswordSetupFormValues>) => {
      try {
        await passwordSetup({}, { ...values, token });
        setSent(true);
      } catch (response) {
        const payload = errorEntryType(response.payload);

        const expiredCode =
          hasCode(payload, VALIDATION_TYPES.TOKEN_EXPIRED) ||
          hasFieldCode(payload, "token", VALIDATION_TYPES.TOKEN_EXPIRED);
        const usedCode =
          hasCode(payload, VALIDATION_TYPES.TOKEN_USED) ||
          hasFieldCode(payload, "token", VALIDATION_TYPES.TOKEN_EXPIRED);
        const notFoundCode =
          hasCode(payload, VALIDATION_TYPES.TOKEN_NOT_FOUND) ||
          hasFieldCode(payload, "token", VALIDATION_TYPES.TOKEN_EXPIRED);

        const tokenError = expiredCode || usedCode || notFoundCode;

        if (tokenError) {
          addMessage({
            type: "warning",
            context: FORM_MESSENGER_CONTEXT,
            text: t("SESSION_PASSWORD_SETUP.TOKEN_EXPIRED_MESSAGE"),
            lifeTime: Infinity,
          });

          return;
        }

        addMessage({
          type: "warning",
          text: t("common:VALIDATION_MESSAGES.GENERAL"),
          context: FORM_MESSENGER_CONTEXT,
        });

        applyApiErrors(actions, response.payload);
        actions.setSubmitting(false);
      }
    };

    const renderSent = () => {
      return (
        <AnimatedLayoutContainer size="sm" showLogo>
          <Flex direction="column" align="center" innerMargin="lg" padding={[false, "lg"]}>
            <ModalHeader size="lg" weight="bold" color="black" align="center">
              {t("SESSION_SIGNUP.USER_FORM.RESET_PASSWORD")}
            </ModalHeader>
            <Img src={CongratulationImg} />
            <Header color="primary" size="sm" align="center">
              {t("SESSION_PASSWORD_RESET.SUCCESS_MESSAGE")}
            </Header>
            <RouteButton
              path={router.get("login").path}
              variant="primary"
              size="lg"
              testName="login"
              className="topbar__link-button">
              {t("LANDING_PAGE.BUTTON_LOGIN")}
            </RouteButton>
          </Flex>
        </AnimatedLayoutContainer>
      );
    };

    const renderForm = (formikProps: FormikProps<IPasswordSetupFormValues>) => (
      <PasswordSetupForm form={formikProps} token={token} />
    );

    const setupPasswordForm = () => {
      return (
        <AnimatedLayoutContainer size="sm" showLogo>
          <Flex direction="column" innerBorder grow={1} padding={["lg", false, false]}>
            <Flex innerMargin="lg" direction="column">
              <Formik
                initialValues={FORM_EMPTY_INITIAL_VALUES}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                render={renderForm}
              />
            </Flex>
          </Flex>
        </AnimatedLayoutContainer>
      );
    };

    if (!passwordStatus) {
      return <LoadingSpinner size="lg" />;
    } else if (passwordStatus === "VALID") {
      return sent ? renderSent() : setupPasswordForm();
    } else {
      return <PasswordResetTokenInvalid />;
    }
  },
);

export const SessionPasswordSetup = withRouteProps(SessionPasswordSetupComponent, transformPasswordSetupRouteProps);
